import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import { useLanguageProfile } from '../../../contexts/LanguageProfileContext';
import { platformAnalyticsService } from '../../../services/platformAnalyticsService';

const DATE_RANGES = [
  { label: '7D', value: 7 },
  { label: '30D', value: 30 },
  { label: '90D', value: 90 },
];

const ACCOUNT_COLORS = [
  '#16A34A', '#2563EB', '#F97316', '#9333EA', '#0891B2',
  '#DB2777', '#CA8A04', '#4F46E5', '#0F766E', '#DC2626',
];

const PLATFORM_CONFIG = {
  facebook: {
    label: 'Facebook', color: '#1877F2',
    metrics: [
      { key: 'reach', label: 'Reach', aggregation: 'sum' },
      { key: 'engagement', label: 'Engagement', aggregation: 'sum' },
      { key: 'clicks', label: 'Clicks', aggregation: 'sum' },
      { key: 'follower_change', label: 'Follower change', aggregation: 'sum', signed: true },
    ],
  },
  instagram: {
    label: 'Instagram', color: '#C13584',
    metrics: [
      { key: 'views', label: 'Views', aggregation: 'sum' },
      { key: 'reach', label: 'Reach', aggregation: 'sum' },
      { key: 'interactions', label: 'Interactions', aggregation: 'sum' },
      { key: 'profile_activity', label: 'Profile activity', aggregation: 'sum' },
      { key: 'follower_change', label: 'Follower change', aggregation: 'sum', signed: true },
    ],
  },
  threads: {
    label: 'Threads', color: '#111827',
    metrics: [
      { key: 'views', label: 'Views', aggregation: 'sum' },
      { key: 'likes', label: 'Likes', aggregation: 'sum' },
      { key: 'replies', label: 'Replies', aggregation: 'sum' },
      { key: 'reposts', label: 'Reposts', aggregation: 'sum' },
    ],
  },
  pinterest: {
    label: 'Pinterest', color: '#E60023',
    metrics: [
      { key: 'impressions', label: 'Impressions', aggregation: 'sum' },
      { key: 'engagements', label: 'Engagements', aggregation: 'sum' },
      { key: 'saves', label: 'Saves', aggregation: 'sum' },
      { key: 'outbound_clicks', label: 'Outbound clicks', aggregation: 'sum' },
    ],
  },
};

const formatDate = (value) => new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', timeZone: 'UTC',
});

const formatMetric = (value, metric, currency) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const formatted = Number(value).toLocaleString(undefined, {
    maximumFractionDigits: metric.currency ? 2 : 0,
    minimumFractionDigits: metric.currency ? 2 : 0,
    signDisplay: metric.signed ? 'exceptZero' : 'auto',
  });
  return metric.currency && currency ? `${formatted} ${currency}` : formatted;
};

const metricRowsByDate = (snapshots, metricName) => {
  const grouped = new Map();
  snapshots.filter((row) => row.metric_name === metricName).forEach((row) => {
    grouped.set(row.metric_date, (grouped.get(row.metric_date) || 0) + row.metric_value);
  });
  return grouped;
};

const metricKpi = (snapshots, metric) => {
  if (metric.aggregation === 'latest') {
    const latestByAccount = new Map();
    snapshots.filter((row) => row.metric_name === metric.key).forEach((row) => {
      const previous = latestByAccount.get(row.account_id);
      if (!previous || row.metric_date > previous.metric_date) latestByAccount.set(row.account_id, row);
    });
    return latestByAccount.size
      ? [...latestByAccount.values()].reduce((sum, row) => sum + row.metric_value, 0)
      : null;
  }
  const grouped = metricRowsByDate(snapshots, metric.key);
  if (!grouped.size) return null;
  const values = [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  return values.reduce((sum, [, value]) => sum + value, 0);
};

const lineData = (days, snapshots, metricName, accounts, hideTrailingZeros = false) => {
  const grouped = new Map();
  snapshots.filter((row) => row.metric_name === metricName).forEach((row) => {
    const accountId = String(row.account_id);
    if (!grouped.has(accountId)) grouped.set(accountId, new Map());
    const values = grouped.get(accountId);
    values.set(row.metric_date, (values.get(row.metric_date) || 0) + row.metric_value);
  });
  const latestAvailableByAccount = new Map();
  if (hideTrailingZeros) {
    snapshots.forEach((row) => {
      const accountId = String(row.account_id);
      const latest = latestAvailableByAccount.get(accountId);
      if (row.metric_value !== 0 && (!latest || row.metric_date > latest)) {
        latestAvailableByAccount.set(accountId, row.metric_date);
      }
    });
  }
  const result = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() - offset);
    const key = date.toISOString().slice(0, 10);
    const point = { date: key, label: formatDate(key) };
    accounts.forEach((account) => {
      const values = grouped.get(String(account.id));
      const value = values?.has(key) ? values.get(key) : null;
      point[account.id] = hideTrailingZeros && value === 0 &&
        (!latestAvailableByAccount.get(String(account.id)) || key > latestAvailableByAccount.get(String(account.id)))
        ? null
        : value;
    });
    result.push(point);
  }
  return result;
};

const statusSummary = (states, accounts = []) => {
  if (!states.length) return { status: 'never', message: 'No synchronization has run for this selection.' };
  const priority = ['reconnect_required', 'permission_required', 'error', 'partial', 'unavailable', 'never', 'ok'];
  const state = [...states].sort((a, b) => priority.indexOf(a.sync_status) - priority.indexOf(b.sync_status))[0];
  const accountNames = new Map(accounts.map((account) => [String(account.id), account.name]));
  const affectedAccounts = states
    .filter((item) => item.sync_status === state.sync_status)
    .map((item) => accountNames.get(String(item.account_id)) || `Account ${item.account_id}`);
  const labels = {
    reconnect_required: 'Reconnect account',
    permission_required: 'Permission required',
    error: 'Synchronization error',
    partial: 'Some metrics unavailable',
    unavailable: 'Data unavailable',
    never: 'Not synchronized yet',
    ok: 'Synchronized',
  };
  const prefix = affectedAccounts.length ? `${affectedAccounts.join(', ')}: ` : '';
  return { status: state.sync_status, message: `${prefix}${state.error_message || labels[state.sync_status]}` };
};

const relativeTime = (timestamp) => {
  if (!timestamp) return 'Never';
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return new Date(timestamp).toLocaleString();
};

const InsightTooltip = ({ active, payload, label, metric, currency }) => {
  const entries = payload?.filter((entry) => entry.value !== null && entry.value !== undefined) || [];
  if (!active || !entries.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-lg px-3 py-2 text-xs min-w-[160px] max-h-48 overflow-y-auto">
      <p className="text-[#64748B]">{label}</p>
      <div className="mt-1.5 space-y-1">
        {entries.map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-[#64748B] flex-1 truncate">{entry.name}</span>
            <span className="font-semibold text-[#0F172A]">{formatMetric(entry.value, metric, currency)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PerformanceInsights = ({ selection }) => {
  const { activeProfile } = useLanguageProfile();
  const accountRequestId = useRef(0);
  const dataRequestId = useRef(0);
  const [platform, setPlatform] = useState('facebook');
  const [metricName, setMetricName] = useState('reach');
  const [days, setDays] = useState(7);
  const [accounts, setAccounts] = useState([]);
  const [hiddenAccountIds, setHiddenAccountIds] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [states, setStates] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const config = PLATFORM_CONFIG[platform];

  const choosePlatform = (nextPlatform) => {
    setPlatform(nextPlatform);
    setHiddenAccountIds([]);
    setMetricName(PLATFORM_CONFIG[nextPlatform].metrics[0].key);
  };

  useEffect(() => {
    setHiddenAccountIds([]);
    setSnapshots([]);
    setStates([]);
  }, [activeProfile?.id]);

  useEffect(() => {
    if (!selection?.platform || !PLATFORM_CONFIG[selection.platform]) return;
    setPlatform(selection.platform);
    setHiddenAccountIds([]);
    setMetricName(PLATFORM_CONFIG[selection.platform].metrics[0].key);
  }, [selection]);

  useEffect(() => {
    const current = ++accountRequestId.current;
    setAccounts([]);
    if (!activeProfile?.id) {
      setAccountsLoading(false);
      return undefined;
    }
    setAccountsLoading(true);
    platformAnalyticsService.getAccounts(activeProfile.id, platform)
      .then((nextAccounts) => {
        if (current === accountRequestId.current) setAccounts(nextAccounts);
      })
      .catch((loadError) => {
        if (current === accountRequestId.current) setError(loadError.message || 'Unable to load accounts.');
      })
      .finally(() => {
        if (current === accountRequestId.current) setAccountsLoading(false);
      });
    return () => { accountRequestId.current += 1; };
  }, [activeProfile?.id, platform, reloadKey]);

  useEffect(() => {
    const current = ++dataRequestId.current;
    setSnapshots([]);
    setStates([]);
    setError(null);
    if (!activeProfile?.id) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    platformAnalyticsService.getInsights({
      profileId: activeProfile.id,
      platform,
      accountId: null,
      days,
    })
      .then((result) => {
        if (current !== dataRequestId.current) return;
        setSnapshots(result.snapshots);
        setStates(result.states);
      })
      .catch((loadError) => {
        if (current === dataRequestId.current) setError(loadError.message || 'Unable to load performance insights.');
      })
      .finally(() => {
        if (current === dataRequestId.current) setLoading(false);
      });
    return () => { dataRequestId.current += 1; };
  }, [activeProfile?.id, days, platform, reloadKey]);

  const availableMetrics = useMemo(() => new Set([
    ...snapshots.map((row) => row.metric_name),
    ...states.flatMap((state) => state.available_metrics || []),
  ]), [snapshots, states]);
  const selectedMetric = config.metrics.find((metric) => metric.key === metricName) || config.metrics[0];
  const accountSeries = useMemo(() => accounts.map((account, index) => ({
    ...account,
    color: ACCOUNT_COLORS[index] || `hsl(${Math.round((index * 137.508) % 360)} 68% 45%)`,
  })), [accounts]);
  const chartData = useMemo(
    () => lineData(days, snapshots, selectedMetric.key, accountSeries, platform === 'pinterest'),
    [accountSeries, days, platform, selectedMetric.key, snapshots],
  );
  const visibleAccountSeries = accountSeries.filter((account) => !hiddenAccountIds.includes(account.id));
  const hasSelectedData = snapshots.some((row) => row.metric_name === selectedMetric.key);
  const connectedAccountIds = new Set(accountSeries.map((account) => String(account.id)));
  const connectedStates = states.filter((state) => connectedAccountIds.has(String(state.account_id)));
  const sync = statusSummary(connectedStates, accountSeries);
  const currency = snapshots.find((row) => row.metric_name === 'revenue' && row.metadata?.currency)?.metadata.currency || null;
  const lastSynced = states.reduce((latest, state) => {
    if (!state.last_synced_at) return latest;
    return !latest || new Date(state.last_synced_at) > new Date(latest) ? state.last_synced_at : latest;
  }, null);

  const toggleAccountLine = (accountId) => {
    setHiddenAccountIds((current) => current.includes(accountId)
      ? current.filter((id) => id !== accountId)
      : [...current, accountId]);
  };

  const refresh = async () => {
    if (!activeProfile?.id || refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      await platformAnalyticsService.refreshInsights({
        profileId: activeProfile.id,
        platform,
        accountId: null,
      });
      setReloadKey((value) => value + 1);
    } catch (refreshError) {
      setError(refreshError.message || 'Insights refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const bannerColors = sync.status === 'ok'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : sync.status === 'error' || sync.status === 'reconnect_required'
      ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <section id="performance-insights" className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 sm:p-6 scroll-mt-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-[#0F172A]">Performance Insights</h2>
          <p className="text-xs text-[#64748B] mt-0.5">Daily metrics synchronized securely by the server</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[11px] text-[#94A3B8]">Last sync: {relativeTime(lastSynced)}</span>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || accountsLoading || accounts.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-[#E5E7EB] text-[#64748B] hover:border-[#CBD5E1] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Icon name="RefreshCw" size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing' : 'Manual Refresh'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {Object.entries(PLATFORM_CONFIG).map(([key, item]) => (
          <button
            type="button"
            key={key}
            onClick={() => choosePlatform(key)}
            className="px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
            style={platform === key
              ? { backgroundColor: `${item.color}12`, borderColor: `${item.color}40`, color: item.color }
              : { borderColor: '#E5E7EB', color: '#94A3B8' }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <label className="text-[11px] font-medium text-[#64748B]">
          Metric
          <select value={metricName} onChange={(event) => setMetricName(event.target.value)} className="mt-1 w-full rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-2 text-xs text-[#0F172A]">
            {config.metrics.map((metric) => (
              <option key={metric.key} value={metric.key}>{metric.label}{availableMetrics.size && !availableMetrics.has(metric.key) ? ' — unavailable' : ''}</option>
            ))}
          </select>
        </label>
        <div className="text-[11px] font-medium text-[#64748B]">
          Range
          <div className="mt-1 flex h-[34px] items-center bg-[#F8FAFC] rounded-lg p-0.5 border border-[#E5E7EB]">
            {DATE_RANGES.map((range) => (
              <button key={range.value} type="button" onClick={() => setDays(range.value)} className={`flex-1 h-full rounded-md text-xs ${days === range.value ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B]'}`}>{range.label}</button>
            ))}
          </div>
        </div>
      </div>

      {!loading && connectedStates.length > 0 && sync.status !== 'ok' && (
        <div className={`mt-4 px-3 py-2 rounded-lg border text-xs flex items-start gap-2 ${bannerColors}`}>
          <Icon name={sync.status === 'reconnect_required' || sync.status === 'error' ? 'AlertCircle' : 'Info'} size={14} className="mt-0.5 flex-shrink-0" />
          <span><strong>{sync.status === 'reconnect_required' || sync.status === 'permission_required' ? 'Reconnect account' : 'Data availability'}:</strong> {sync.message}</span>
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mt-4">
        {config.metrics.slice(0, 4).map((metric) => {
          const value = metricKpi(snapshots, metric);
          return (
            <div key={metric.key} className="rounded-xl border border-[#F1F5F9] bg-[#FAFCFE] p-3.5 min-w-0">
              <p className="text-[11px] text-[#64748B] truncate">{metric.label}</p>
              <p className="text-xl font-bold text-[#0F172A] mt-1 truncate">{formatMetric(value, metric, currency)}</p>
              <p className="text-[10px] text-[#94A3B8] mt-0.5">{value === null ? 'Unavailable' : metric.aggregation === 'latest' ? 'Latest snapshot' : `${days}-day total`}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-xl border border-[#F1F5F9] p-3 sm:p-4" style={{ height: 290 }}>
        {loading || accountsLoading ? (
          <div className="h-full flex items-center justify-center" role="status" aria-label="Loading performance insights">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: config.color }} />
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <Icon name="AlertCircle" size={28} className="text-red-400 mb-2" />
            <p className="text-sm font-medium text-[#0F172A]">Insights could not be loaded</p>
            <p className="text-xs text-[#64748B] mt-1 max-w-lg">{error}</p>
            <button type="button" onClick={() => setReloadKey((value) => value + 1)} className="mt-3 text-xs font-semibold text-blue-600">Try again</button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-[#94A3B8] gap-2 text-center">
            <Icon name="Link2Off" size={28} className="opacity-40" />
            <p className="text-sm">No connected {config.label} accounts for this profile</p>
          </div>
        ) : !hasSelectedData ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <Icon name={sync.status === 'reconnect_required' ? 'Unplug' : 'LineChart'} size={28} className="text-[#CBD5E1] mb-2" />
            <p className="text-sm font-medium text-[#0F172A]">{sync.status === 'reconnect_required' ? 'Reconnect account' : sync.status === 'permission_required' ? 'Permission required' : 'Data unavailable'}</p>
            <p className="text-xs text-[#64748B] mt-1 max-w-lg">{sync.message}</p>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 pb-2 max-h-12 overflow-y-auto" aria-label={`${config.label} account visibility`}>
              {accountSeries.map((account) => {
                const visible = !hiddenAccountIds.includes(account.id);
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => toggleAccountLine(account.id)}
                    aria-pressed={visible}
                    className={`flex items-center gap-1.5 text-[11px] font-medium transition-opacity ${visible ? 'text-[#475569]' : 'text-[#94A3B8] opacity-40'}`}
                  >
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: account.color }} />
                    <span>{account.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="min-h-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 10, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} interval={days <= 7 ? 0 : days <= 30 ? 4 : 13} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={selectedMetric.currency} />
                  <Tooltip content={<InsightTooltip metric={selectedMetric} currency={currency} />} />
                  {visibleAccountSeries.map((account) => (
                    <Line
                      key={account.id}
                      type="monotone"
                      dataKey={account.id}
                      name={account.name}
                      stroke={account.color}
                      strokeWidth={2.25}
                      dot={days <= 7 ? { r: 3, fill: account.color } : false}
                      activeDot={{ r: 4 }}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PerformanceInsights;
